/**
 * mapErrorSlug() — Finding F central error-slug → i18n-key mapper.
 *
 * Plan 02-10a Task 3 (Run 3 EVA-UAT raw-slug toast-UX gap close).
 *
 * Backend handlers return FastAPI ``HTTPException(detail="<slug>")`` where
 * ``<slug>`` is a stable machine-readable key (e.g., ``insufficient_permission``,
 * ``position_not_in_draft``). Previously the frontend surfaced these directly
 * as raw slug strings (e.g., toast.error("insufficient_permission")) OR fell
 * back to a generic "Verifique su conexión" / "Save failed" message that
 * misled users about the actual problem.
 *
 * This helper detects FastAPI-style slug payloads (lowercase + underscores
 * only) and looks up a localized translation under the ``errors.slug.*`` i18n
 * namespace. Composition with ``formatValidationError`` is transparent:
 *   - slug match → return localized string
 *   - slug missing from i18n → fall through to formatValidationError
 *   - non-slug detail shape (array, object) → delegate to formatValidationError
 *   - non-ApiError → delegate to formatValidationError
 *
 * No breaking change to existing formatValidationError callsites; new callsites
 * (PositionAssigneesTab 403, PositionWizard PATCH 409) gain localized messages
 * without losing the BUG-010 fallback chain.
 *
 * Cross-app skill candidate (Sat 2026-05-30 audit): all Stefan apps that
 * surface raw FastAPI slug detail strings to user-facing toasts benefit.
 */
import i18next from "i18next";

import { ApiError } from "@/types/api";

import { formatValidationError } from "./api";

/** Slug shape: lowercase letters + underscores only, matches FastAPI convention. */
const SLUG_RE = /^[a-z_]+$/;

/**
 * Maps a FastAPI-style error slug to a localized user-facing message.
 *
 * @param err Any error thrown / received by a TanStack Query mutation or fetch.
 * @param namespace i18n namespace prefix (default ``"errors"``); slug becomes
 *                  ``<namespace>.slug.<detail>`` key.
 * @returns Localized translation if slug found; otherwise falls back to
 *          ``formatValidationError(err)`` which has its own BUG-010 fallback
 *          chain (extracts msg from detail array, then generic message).
 *
 * @example
 *   // ApiError(status=403, body={detail: "insufficient_permission"})
 *   mapErrorSlug(err);  // → "Permiso insuficiente" (ES) / "Insufficient permission" (EN)
 *
 * @example
 *   // ApiError(status=422, body={detail: [{loc: ["body", "title"], msg: "required", type: "..."}]})
 *   mapErrorSlug(err);  // → "required" via formatValidationError (non-slug shape)
 */
export function mapErrorSlug(err: unknown, namespace = "errors"): string {
  if (!(err instanceof ApiError)) {
    return formatValidationError(err);
  }
  const detail = err.body.detail;
  if (typeof detail !== "string" || !SLUG_RE.test(detail)) {
    return formatValidationError(err);
  }
  // i18next ns-separator defaults to ":", not ".", so we MUST pass the
  // namespace via the options bag rather than `<ns>.slug.<detail>` flat key
  // (BUG that surfaced in initial Plan 02-10a Task 3 test run: flat key
  // returned empty translation, falling through to formatValidationError).
  const translated = i18next.t(`slug.${detail}`, {
    ns: namespace,
    defaultValue: "",
  });
  return translated || formatValidationError(err);
}
